import { useDidUpdate } from '@mantine/hooks';
import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook to manage scroll area width with animation-aware updates
 *
 * When tasksOpen changes, temporarily sets width to 0 during animation (100ms)
 * to avoid expensive ScrollArea recalculations, then updates to the final width
 * after animation completes.
 *
 * @param {number} containerWidth - The current container width from useElementSize
 * @param {boolean} tasksOpen - Boolean indicating if the tasks panel is open
 * @returns {number} The scroll area width to use
 */
const useScrollAreaWidth = (containerWidth: number, tasksOpen: boolean): number => {
  const [scrollAreaWidth, setScrollAreaWidth] = useState(containerWidth);

  // Use ref to always access the latest containerWidth value
  const containerWidthRef = useRef(containerWidth);

  // Update ref whenever containerWidth changes
  useEffect(() => {
    containerWidthRef.current = containerWidth;
  }, [containerWidth]);

  // Update scroll area width when container width changes (window resize, etc.)
  useEffect(() => {
    setScrollAreaWidth(containerWidth);
  }, [containerWidth]);

  // Handle animation when tasks panel opens/closes
  useDidUpdate(() => {
    // Set to 0 during animation to avoid recalculations
    setScrollAreaWidth(0);

    // After animation completes, update to actual width
    const timeoutId = setTimeout(() => {
      setScrollAreaWidth(containerWidthRef.current);
    }, 100);

    // Cleanup: clear timeout if component unmounts or dependencies change
    return () => clearTimeout(timeoutId);
  }, [tasksOpen]);

  return scrollAreaWidth;
};

export { useScrollAreaWidth };
